import { zodResolver } from '@hookform/resolvers/zod';
import { subYears } from 'date-fns';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Pressable, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { z } from 'zod';

import { Card, ControlledInput, Text } from '@/ui';
import { DatetimePopoverField } from '@/ui/datetime-popover-field';
import { Icon } from '@/ui/icon';
import { TrashFill } from '@/ui/icons/trash-fill';

export const addChargingRecordFormSchema = z.object({
  chargingStationName: z.string().min(2, 'Minimum 2 characters'),
  entryDate: z.date(),
  amount: z.number(),
});

export type AddChargingRecordFormType = z.infer<
  typeof addChargingRecordFormSchema
>;

export default function ChargingRecordForm({
  index,
  onChange,
  onValidChange,
  onRemove,
  onCalendarOpenChange,
  pickupTz,
}: {
  index: number;
  onChange: (index: number, values: AddChargingRecordFormType) => void;
  onValidChange: (index: number, isValid: boolean) => void;
  onRemove: (index: number) => void;
  onCalendarOpenChange: (isOpen: boolean) => void;
  pickupTz: string;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const {
    control,
    trigger,
    setValue,
    formState: { isValid },
    getValues,
    reset,
    watch,
  } = useForm<AddChargingRecordFormType>({
    resolver: zodResolver(addChargingRecordFormSchema),
  });

  const [entryDate] = useWatch({
    control,
    name: ['entryDate'],
  });

  useEffect(() => {
    onValidChange(index, isValid);
  }, [isValid]);

  // Notify parent when values change
  useEffect(() => {
    const sub = watch(() => {
      onChange(index, getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  return (
    <Card style={{ gap: 16, padding: 16 }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text type="bodyRegular" style={{ color: theme.colors.text.secondary }}>
          Charging record {index + 1}
        </Text>

        {index > 0 ? (
          <Pressable
            onPress={() => {
              onRemove(index);
            }}
          >
            <Icon
              type="dressed"
              icon={<TrashFill />}
              width={12}
              height={12}
              fillColor={theme.colors.icon.errorColor}
            />
          </Pressable>
        ) : null}
      </View>

      <ControlledInput
        control={control}
        name="chargingStationName"
        label="Charging Station Name"
        onBlur={() => {
          trigger('chargingStationName');
        }}
        onChangeText={(text) => {
          trigger('chargingStationName');
          setValue('chargingStationName', text);
        }}
      />

      <View
        style={{
          flexDirection: 'row',
          zIndex: 2,
          alignItems: 'center',
          gap: 16,
        }}
      >
        <DatetimePopoverField
          id="entry-date-calendar"
          label="Entry Date"
          value={entryDate}
          minDate={subYears(new Date(), 2)}
          timeZone={pickupTz}
          onChange={(d) => {
            setValue('entryDate', d);
          }}
          onOpenChange={onCalendarOpenChange}
          containerStyle={{ width: 275 }}
        />

        <ControlledInput
          control={control}
          name="amount"
          label="Amount"
          onBlur={() => {
            trigger('amount');
          }}
          onChangeText={(text) => {
            trigger('amount');
            setValue('amount', parseFloat(text));
          }}
        />
      </View>
    </Card>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    marginTop: 48,
    paddingBottom: {
      xs: 16,
      md: 0,
    },
    justifyContent: {
      xs: 'flex-end',
      md: 'flex-start',
    },
    alignSelf: 'center',
    justifyItems: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_1,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 24,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
}));
